import { useTranslation } from "react-i18next";

import {
  computeStatusFreshness,
  formatAgeCompact,
  type TtlFreshness,
  type TtlState,
} from "../../lib/freshness";

import type { StationStatus } from "./api";

// Auto-degrade rule (spec §B.3): once TTL is exceeded, displayed status is
// "unknown" regardless of what the chargers table says.
function effectiveStatus(rawStatus: StationStatus, state: TtlState): StationStatus {
  if (state === "expired" || state === "no_data") return "unknown";
  return rawStatus;
}

type Size = "sm" | "md";

type Props = {
  status: StationStatus;
  lastActivityAt: string | null;
  // Optional test overrides — kept on the public API so tests can mock the
  // clock and TTL without monkey-patching Date.
  ttlMs?: number;
  now?: Date;
  size?: Size;
  testIdSuffix?: string;
};

const TONE: Record<
  TtlFreshness["state"],
  { dot: string; track: string; bar: string; label: string; ring: string }
> = {
  fresh: {
    dot: "bg-volt shadow-[0_0_12px_-2px_var(--color-volt-glow)] animate-[live-pulse_2s_ease-out_infinite]",
    track: "bg-volt/10",
    bar: "bg-volt",
    label: "text-volt-deep",
    ring: "ring-1 ring-volt/30",
  },
  aging: {
    dot: "bg-amber",
    track: "bg-amber/10",
    bar: "bg-amber",
    label: "text-amber",
    ring: "ring-1 ring-amber/25",
  },
  stale: {
    dot: "bg-transparent border border-ember",
    track: "bg-ember/10",
    bar: "bg-ember",
    label: "text-ember",
    ring: "ring-1 ring-ember/25",
  },
  expired: {
    dot: "bg-transparent border border-fg-faint",
    track: "bg-fg-faint/15",
    bar: "bg-fg-faint",
    label: "text-fg-mute",
    ring: "ring-1 ring-fg-faint/25",
  },
  no_data: {
    dot: "bg-transparent border border-dashed border-fg-faint",
    track: "bg-fg-faint/10",
    bar: "bg-transparent",
    label: "text-fg-faint",
    ring: "ring-1 ring-fg-faint/20",
  },
};

const STATUS_TONE: Record<StationStatus, string> = {
  available: "text-volt-deep",
  waiting: "text-amber",
  offline: "text-ember",
  unknown: "text-fg-mute",
};

const STATUS_KEY: Record<StationStatus, string> = {
  available: "stations.status_available",
  waiting: "stations.status_waiting",
  offline: "stations.status_offline",
  unknown: "stations.status_unknown",
};

export function StatusFreshnessBadge({
  status,
  lastActivityAt,
  ttlMs,
  now,
  size = "md",
  testIdSuffix = "",
}: Props) {
  const { t } = useTranslation();
  const freshness = computeStatusFreshness(lastActivityAt, { ttlMs, now });
  const display = effectiveStatus(status, freshness.state);
  const tone = TONE[freshness.state];

  const ageLabel = freshness.ageMs === null ? null : formatAgeCompact(freshness.ageMs);
  const ariaLabel = buildAriaLabel(t, display, freshness, ageLabel);

  // Decay gauge: full when fresh, drained as TTL elapses, empty when expired.
  // 1 - decay so the bar visually "drains" toward expiry.
  const fillPct = Math.round((1 - freshness.decay) * 100);

  const padding = size === "sm" ? "px-2 py-1" : "px-2.5 py-1.5";
  const dotSize = size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2";

  const suffix = testIdSuffix ? `-${testIdSuffix}` : "";

  return (
    <div
      data-testid={`status-freshness${suffix}`}
      data-state={freshness.state}
      data-status={display}
      role="group"
      aria-label={ariaLabel}
      className={`group/badge relative inline-flex flex-col gap-1 rounded-md bg-surface-sunk ${padding} ${tone.ring}`}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={`inline-block rounded-full ${dotSize} ${tone.dot}`}
        />
        <span
          className={`num text-[10px] font-semibold uppercase tracking-[0.16em] ${STATUS_TONE[display]}`}
        >
          {t(STATUS_KEY[display])}
        </span>
        {ageLabel && (
          <>
            <span aria-hidden="true" className="text-fg-faint">
              ·
            </span>
            <span
              className={`num text-[10px] tracking-[0.04em] ${tone.label}`}
              data-testid={`status-freshness-age${suffix}`}
            >
              {ageLabel}
            </span>
          </>
        )}
        {freshness.state === "no_data" && (
          <span className={`num text-[10px] tracking-[0.04em] ${tone.label}`}>
            —
          </span>
        )}
      </div>
      <div
        aria-hidden="true"
        className={`relative h-[2px] w-full overflow-hidden rounded-full ${tone.track}`}
      >
        <div
          className={`absolute inset-y-0 left-0 ${tone.bar} transition-[width] duration-500`}
          style={{ width: `${fillPct}%` }}
        />
      </div>
      {freshness.state === "stale" && (
        <span className="num text-[9px] uppercase tracking-[0.18em] text-ember/80">
          {t("freshness_badge.stale_hint")}
        </span>
      )}
      {freshness.state === "expired" && (
        <span className="num text-[9px] uppercase tracking-[0.18em] text-fg-faint">
          {t("freshness_badge.expired_hint")}
        </span>
      )}
    </div>
  );
}

function buildAriaLabel(
  t: ReturnType<typeof useTranslation>["t"],
  display: StationStatus,
  freshness: TtlFreshness,
  ageLabel: string | null,
): string {
  const statusText = t(STATUS_KEY[display]);
  if (freshness.state === "no_data") {
    return `${statusText} — ${t("freshness_badge.aria_no_data")}`;
  }
  return `${statusText} — ${t("freshness_badge.aria_reported", { age: ageLabel ?? "" })}`;
}
